import React from 'react';
import { Helmet } from 'react-helmet-async';
interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
}
export function SEO({
  title = 'Mateo Cohen - Lead AI Product Designer | UX/UI Design Portfolio',
  description = 'Mateo Cohen (John M Cohen) - Lead AI Product Designer with 8+ years experience in UX research, enterprise applications, and design systems. Specialized in AI innovation, financial services, and cybersecurity. Featured at AWS re:Invent 2025.',
  keywords = [
  // Name Variations for Search Recognition
  'Mateo Cohen', 'John Cohen', 'John M Cohen', 'Mateo John Cohen',
  // Primary Job Titles (High Priority)
  'AI Product Designer', 'Lead Product Designer', 'Senior UX Designer', 'Senior UI Designer', 'UX Designer', 'UI Designer', 'Product Designer',
  // AI & Technology Focus
  'AI Design', 'AI Product Design', 'AI Innovation', 'AI UX Designer', 'Conversational AI Designer', 'Machine Learning Designer', 'AI Interface Design', 'Prompt Engineering', 'AI Strategy',
  // Core Competencies
  'User Experience Design', 'User Interface Design', 'UX Research', 'User Research', 'Design Systems', 'Design System Architecture', 'Interaction Design', 'Prototyping', 'Usability Testing',
  // Industry Specializations
  'Enterprise UX', 'B2B Design', 'B2B UX Designer', 'SaaS Design', 'SaaS UX Designer', 'Financial Services Design', 'Fintech Designer', 'Banking UX', 'Cybersecurity UX',
  // Leadership & Strategy
  'Design Leadership', 'Product Strategy', 'Design Strategy', 'UX Strategy', 'Design Team Lead',
  // Location-based (Local SEO)
  'Cleveland Designer', 'Cleveland UX Designer', 'Ohio Designer', 'Chicago Designer', 'Chicago UX Designer', 'Pittsburgh Designer', 'Remote Designer', 'Remote UX Designer',
  // Technical Skills
  'Figma Designer', 'Accessibility Design', 'WCAG Compliance', 'Responsive Design', 'Mobile Design', 'Dashboard Design',
  // Portfolio & Availability
  'UX Portfolio', 'Design Portfolio', 'Available January 2026', 'Freelance Designer', 'Contract Designer'],
  image = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/mateo-cohen-portfolio-og.png',
  url = 'https://mateocohen.com',
  type = 'website',
  author = 'Mateo Cohen',
  publishedTime,
  modifiedTime,
  section
}: SEOProps) {
  const fullTitle = title.includes('Mateo Cohen') ? title : `${title} | Mateo Cohen`;
  return <Helmet data-id="element-430">
      {/* Primary Meta Tags */}
      <title data-id="element-431">{fullTitle}</title>
      <meta name="title" content={fullTitle} data-id="element-432" />
      <meta name="description" content={description} data-id="element-433" />
      <meta name="keywords" content={keywords.join(', ')} data-id="element-434" />
      <meta name="author" content={author} data-id="element-435" />
      <link rel="canonical" href={url} data-id="element-436" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} data-id="element-437" />
      <meta property="og:url" content={url} data-id="element-438" />
      <meta property="og:title" content={fullTitle} data-id="element-439" />
      <meta property="og:description" content={description} data-id="element-440" />
      <meta property="og:image" content={image} data-id="element-441" />
      <meta property="og:site_name" content="Mateo Cohen Portfolio" data-id="element-442" />
      <meta property="og:locale" content="en_US" data-id="element-443" />

      {/* Article specific tags */}
      {type === 'article' && publishedTime && <meta property="article:published_time" content={publishedTime} data-id="element-444" />}
      {type === 'article' && modifiedTime && <meta property="article:modified_time" content={modifiedTime} data-id="element-445" />}
      {type === 'article' && author && <meta property="article:author" content={author} data-id="element-446" />}
      {type === 'article' && section && <meta property="article:section" content={section} data-id="element-447" />}

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" data-id="element-448" />
      <meta property="twitter:url" content={url} data-id="element-449" />
      <meta property="twitter:title" content={fullTitle} data-id="element-450" />
      <meta property="twitter:description" content={description} data-id="element-451" />
      <meta property="twitter:image" content={image} data-id="element-452" />
      <meta name="twitter:creator" content="@mateocohen" data-id="element-453" />

      {/* Additional SEO tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" data-id="element-454" />
      <meta name="googlebot" content="index, follow" data-id="element-455" />
      <meta name="bingbot" content="index, follow" data-id="element-456" />

      {/* Geo tags for local SEO */}
      <meta name="geo.region" content="US-OH" data-id="element-457" />
      <meta name="geo.placename" content="Cleveland" data-id="element-458" />
      <meta name="geo.position" content="41.4993;-81.6944" data-id="element-459" />
      <meta name="ICBM" content="41.4993, -81.6944" data-id="element-460" />

      {/* Language */}
      <meta httpEquiv="content-language" content="en-US" data-id="element-461" />

      {/* Mobile optimization */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" data-id="element-462" />
      <meta name="theme-color" content="#000000" data-id="element-463" />

      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" data-id="element-464" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" data-id="element-465" />
      <link rel="preconnect" href="https://cdn.jsdelivr.net" data-id="element-466" />
      <link rel="dns-prefetch" href="https://mirrorful-production.s3.us-west-1.amazonaws.com" data-id="element-467" />

      {/* Google Analytics */}
      <script async src="https://www.googletagmanager.com/gtag/js?id=G-4RMMCHMLXC" data-id="element-468" />
      <script data-id="element-469">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-4RMMCHMLXC');
        `}
      </script>
    </Helmet>;
}